import { CdkTextareaAutosize } from '@angular/cdk/text-field';
import { CurrencyPipe, NgFor, NgIf } from '@angular/common';
import {
  Component,
  Inject,
  OnDestroy,
  OnInit,
  ViewContainerRef,
  ViewEncapsulation
} from '@angular/core';
import { FlexModule } from '@angular/flex-layout';
import { ExtendedModule } from '@angular/flex-layout/extended';
import {
  ReactiveFormsModule,
  UntypedFormControl,
  UntypedFormGroup,
  Validators
} from '@angular/forms';
import { MatButton } from '@angular/material/button';
import { MatOption } from '@angular/material/core';
import {
  MatDatepicker,
  MatDatepickerInput,
  MatDatepickerToggle
} from '@angular/material/datepicker';
import {
  MAT_DIALOG_DATA,
  MatDialog,
  MatDialogActions,
  MatDialogContent,
  MatDialogRef,
  MatDialogTitle
} from '@angular/material/dialog';
import {
  MatError,
  MatFormField,
  MatLabel,
  MatPrefix,
  MatSuffix
} from '@angular/material/form-field';
import { MatIcon } from '@angular/material/icon';
import { MatInput } from '@angular/material/input';
import { MatSelect } from '@angular/material/select';
import { MatSlideToggle } from '@angular/material/slide-toggle';
import { format, parse } from 'date-fns';
import moment from 'moment';
import {
  NgxMatTimepickerComponent,
  NgxMatTimepickerModule
} from 'ngx-mat-timepicker';
import { merge, Observable, Subject } from 'rxjs';
import {
  catchError,
  filter,
  map,
  startWith,
  switchMap,
  takeUntil
} from 'rxjs/operators';
import { HireItemService, ProductService } from '../..';
import { DepotLocation, HireItem, Item, Product } from '../../../models';
import { PriceSuggestion } from '../../../models/product-hire-estimate';
import { AddCurrencyPrefixDirective } from '../../directives/add-currency-prefix.directive';
import { joinDateTimeMoments } from '../../helpers/joinDateTimeMoments';
import { SelectableHireItem } from '../../hire-items-list/hire-items-list.component';
import { ConfigService } from '../../services/config.service';
import {
  HirePriceStructureDialogComponent,
  HirePriceStructureDialogData,
  PriceStructureDialogResponse
} from '../hire-price-structure-dialog/hire-price-structure-dialog.component';
import {
  HIREABLE_PRICE_UPDATE_IJ,
  HireablePriceUpdate
} from '../hire-price-structure-dialog/hireable-price-update';

@Component({
  selector: 'app-edit-on-hire-dialog',
  templateUrl: './edit-on-hire-dialog.component.html',
  styles: [
    `
      p.off-hire-disabled {
        color: #8a8a8a;
      }

      .hidden {
        display: none;
      }
    `
  ],
  providers: [HireItemService, ProductService],
  encapsulation: ViewEncapsulation.None,
  standalone: true,
  imports: [
    MatDialogTitle,
    ReactiveFormsModule,
    MatDialogContent,
    MatFormField,
    MatLabel,
    MatInput,
    MatDatepickerInput,
    NgIf,
    MatError,
    MatDatepickerToggle,
    MatSuffix,
    MatDatepicker,
    MatIcon,
    MatSelect,
    NgFor,
    MatOption,
    MatButton,
    FlexModule,
    CdkTextareaAutosize,
    ExtendedModule,
    MatDialogActions,
    NgxMatTimepickerModule
  ]
})
export class EditOnHireDialogComponent implements OnInit, OnDestroy {
  item: SelectableHireItem;
  update: HireItem;
  zeroPriceReasonInputVisible = false;
  locations: DepotLocation[];
  currentLocation: DepotLocation;
  offHire = false;
  maximumDate = moment().add(14, 'days');
  private ngUnsubscribe = new Subject<void>();
  suggestedPrice: PriceSuggestion;
  fixedPrice: number = null;

  hireItemForm = new UntypedFormGroup({
    quantity: new UntypedFormControl(1, Validators.required),
    on_hire: new UntypedFormControl(null, Validators.required),
    on_hire_time: new UntypedFormControl(null, Validators.required),
    location_out: new UntypedFormControl(null, Validators.required),
    notes: new UntypedFormControl()
  });

  // 24hr time regex pattern
  timePattern = /^(0[0-9]|1[0-9]|2[0-3]):[0-5][0-9]$/;

  hirePeriodFields: Observable<{
    from: moment.Moment | null;
    to: moment.Moment | null;
  }>;
  errors = {};
  currency = 'GBP';

  constructor(
    public dialogRef: MatDialogRef<EditOnHireDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data,
    @Inject(HIREABLE_PRICE_UPDATE_IJ)
    public hireablePriceUpdateService: HireablePriceUpdate,
    private hireItemService: HireItemService,
    private configService: ConfigService,
    private dialog: MatDialog,
    private vcr: ViewContainerRef
  ) {
    this.item = data.item;
    this.currency = data.currency;
    this.offHire = !!this.item.off_hire;
    this.setFormValues();
    if (this.offHire) {
      this.addOffHireFields();
    } else {
      this.addEstimatedOffHireFields();
    }
  }

  ngOnInit() {
    this.configService.locations
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe((locations) => {
        if (locations) {
          this.locations = locations;
        }
      });
    this.configService.currentLocation
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe((currentLocation) => {
        if (currentLocation) {
          this.currentLocation = currentLocation;
        }
      });
  }

  get productName() {
    if (this.item.hireable_type === 'item') {
      return (
        (this.item.hireable as Item).product?.name +
        ' (' +
        (this.item.hireable as Item).code +
        ')'
      );
    }
    return (this.item.hireable as Product).name;
  }

  save() {
    const {
      on_hire,
      on_hire_time,
      off_hire,
      off_hire_time,
      estimated_off_hire,
      estimated_off_hire_time,
      quantity,
      free_accessory,
      zero_price_reason,
      ...rest
    } = this.hireItemForm.value;

    const update: HireItem = {
      ...rest,
      on_hire: parse(on_hire_time, 'HH:mm', on_hire.toDate()),
      off_hire: off_hire
        ? parse(off_hire_time, 'HH:mm', off_hire.toDate())
        : null,
      estimated_off_hire: estimated_off_hire
        ? parse(
            estimated_off_hire_time ?? '09:00',
            'HH:mm',
            estimated_off_hire.toDate()
          )
        : null,
      quantity: this.hireItemForm.controls['quantity'].disabled ? 1 : quantity
    };
    this.hireItemService
      .update(this.item.id, this.item.docket_id, update)
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe((response) => {
        this.item = Object.assign(this.item, response);
        this.dialogRef.close(this.item);
      });
  }

  private addOffHireFields() {
    const offHire = moment(this.item.off_hire);
    const dateControl = new UntypedFormControl(offHire, Validators.required);
    this.hireItemForm.addControl('off_hire', dateControl);
    const timeControl = new UntypedFormControl(
      format(offHire.toDate(), 'HH:mm'),
      Validators.required
    );
    this.hireItemForm.addControl('off_hire_time', timeControl);
    this.hireItemForm.addControl(
      'location_in',
      new UntypedFormControl(this.item.location_in, Validators.required)
    );
  }

  private addEstimatedOffHireFields() {
    const estOffHire = this.item.estimated_off_hire
      ? moment(this.item.estimated_off_hire)
      : null;
    const dateControl = new UntypedFormControl(estOffHire);
    const timeControl = new UntypedFormControl(
      estOffHire ? format(estOffHire.toDate(), 'HH:mm') : null
    );
    this.hireItemForm.addControl('estimated_off_hire', dateControl);
    this.hireItemForm.addControl('estimated_off_hire_time', timeControl);
  }

  private setFormValues() {
    if (
      (this.item.hireable_type === 'item' &&
        !(this.item.hireable as Item).quantifiable) ||
      this.item.hireable_type === 'product'
    ) {
      this.hireItemForm.get('quantity').disable();
    }
    this.hireItemForm.patchValue(this.item);
    if (this.hireItemForm.get('on_hire').value) {
      const onHireMoment = moment(this.hireItemForm.get('on_hire').value);
      const onHireTime = format(
        new Date(this.hireItemForm.get('on_hire').value),
        'HH:mm'
      );
      this.hireItemForm.get('on_hire').patchValue(onHireMoment);
      this.hireItemForm.get('on_hire_time').patchValue(onHireTime);
    }
  }

  updateTime(event: any, field: string) {
    this.hireItemForm.get(field).patchValue(event);
  }

  updateTimeEnteredManually(field: string, picker: NgxMatTimepickerComponent) {
    const control = this.hireItemForm.get(field);
    if (!control.value) {
      return;
    }
    // 24 hr patterns to try and parse
    const parsePatterns = ['HH:mm', 'HH.mm', 'H:mm', 'HHmm', 'Hmm', 'HH', 'H'];
    const invalidValue = control.value;
    for (const pattern of parsePatterns) {
      const parsed = parse(invalidValue, pattern, new Date());
      if (parsed.toString() !== 'Invalid Date') {
        control.patchValue(format(parsed, 'HH:mm'));
        return picker.updateTime(control.value);
      }
    }
    control.setErrors({ invalidTime: true });
  }

  openPricingDetails() {
    const dialogRef = this.dialog.open<
      HirePriceStructureDialogComponent,
      HirePriceStructureDialogData,
      PriceStructureDialogResponse
    >(HirePriceStructureDialogComponent, {
      data: {
        productId:
          this.item.hireable_type === 'item'
            ? (this.item.hireable as Item).product_id
            : (this.item.hireable as Product).id,
        customerId: this.data.customerId,
        hireableId: this.item.id,
        hireableType: 'hireItem',
        currency: this.currency,
        pricingType: (this.item.pricing_type ?? 'default') as
          | 'default'
          | 'fixed'
          | 'custom'
          | 'no_charge',
        fixedPrice: this.item.charge,
        itemIsOffHired: this.item.off_hire !== null,
        free_accessory: this.item.free_accessory === 1,
        zero_price_reason: this.item.zero_price_reason,
        parentId: this.item.parent_id,
        quantity: this.hireItemForm.get('quantity').value,
        onHire: this.hireItemForm.get('on_hire').value,
        offHire: this.hireItemForm.get('off_hire')?.value,
        currencyRate: this.data.currencyRate
      },
      viewContainerRef: this.vcr
    });
    dialogRef.afterClosed().subscribe((result) => {
      if (result) {
        this.item = {
          ...this.item,
          ...result,
          free_accessory: result.free_accessory ? 1 : 0
        };
      }
    });
  }

  get pricingSummary() {
    if (this.item.pricing_type === 'default') {
      return 'Default pricing structure';
    }
    if (this.item.pricing_type === 'fixed') {
      return `Fixed price`;
    }
    if (this.item.pricing_type === 'custom') {
      return 'Custom pricing structure';
    }
    if (this.item.pricing_type === 'no_charge') {
      return 'No charge';
    }
    return 'Default pricing structure';
  }

  ngOnDestroy() {
    this.ngUnsubscribe.next();
    this.ngUnsubscribe.complete();
  }
}
